import { useContext, useEffect, useMemo } from 'react';
import GameContext from 'contexts/GameContext';
import GlobalContext from 'contexts/GlobalContext';
// ScaleContext removed — no more transform: scale()
import { useAppDispatch, useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';
import _ from 'lodash';
import { showJoinButton } from 'reducers/currentTable';

import { hasStakeInHand } from './MultiRunActions/hasStakeInHand';
import { useMaxRunCount } from './MultiRunActions/maxRunCount';
import { offeredRunCounts, parseOffer } from './MultiRunActions/multiRunPhase';
import VoteAnalyticsWatch from './MultiRunActions/VoteAnalyticsWatch';
import VoteDeliveryWatch from './MultiRunActions/VoteDeliveryWatch';
import WithholdExposure from './MultiRunActions/WithholdExposure';
import StraddlePreActionButton from './TableActions/StraddlePreActionButton';
import StraddleTapWatch from './TableActions/StraddleTapWatch';
import JoinActions from './JoinActions';
import JoinButton from './JoinButton';
import MultiRunActions from './MultiRunActions';
import PokerActions from './PokerActions';
import { ServerTimeoutProvider } from './ServerTimeoutBanner';
import TopAction from './TopAction';

import './UserActions.scss';

const UserActions = () => {
  const dispatch = useAppDispatch();
  const { isJoining, openAskBuyin } = useContext(GlobalContext);

  const game = useTableSelector((table) => table.game);
  const { hand, runs, languageKey } = game;
  const tableType = useTableSelector((table) => table.game.tabletype);
  const noPlayers = useTableSelector((table) => table.gameState.noPlayers);
  const ingamePlayers = useTableSelector((table) => table.gameState.ingamePlayers);
  const nextBBPlayer = useTableSelector((table) => table.gameState.nextBBPlayer);

  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const isPlayerInactive = useTableSelector((table) => table.currentPlayer.isPlayerInactive);
  const isPlayerFolded = _.isUndefined(game['p' + seatId + 'bet'])
    ? false
    : game['p' + seatId + 'bet'].substring(0, 1) === 'F';

  // EN-663: the same money test `isSeatContestingPot` applies server-side.
  // Shared with `useActiveTables`' cross-table vote signal so the two gates
  // cannot drift — see the module for why that matters.
  const seatHasStakeInHand = hasStakeInHand(game, seatId);
  const playerStatus = useTableSelector((table) => table.players[seatId]?.status);
  const isJoined = useTableSelector((table) => table.currentPlayer.isJoined);
  const isRejoining = useTableSelector((table) => table.currentPlayer.isRejoining);
  const isWaiting = useTableSelector((table) => table.currentPlayer.isWaiting);
  const isWaitingAndExit = useTableSelector((table) => table.currentPlayer.isWaitingAndExit);

  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);
  const { setPreselectedAction, playerMultiRunVote } = useContext(GameContext);
  // EN-10921: 3 normally, 2 while the ops switch withholds RUN IT 3X.
  const maxRunCount = useMaxRunCount();

  useEffect(() => {
    if (playerStatus === 4) setPreselectedAction('postblind');
    if (playerStatus === 5) setPreselectedAction('waitforbb');
  }, [playerStatus, setPreselectedAction]);

  // Mirrors Unity TablesView.cs:2339 — when the table view is set up for
  // a given game, ShowJoinButton(true) is called. In v2, UserActions
  // mounts when a table is open; dispatch once per gameID.
  const gameID = useTableSelector((table) => table.game.gameID);
  useEffect(() => {
    if (gameID) {
      dispatch(showJoinButton(true));
    }
  }, [gameID, dispatch]);

  // Mirrors Unity BuyInView.cs:121 — when the buy-in view opens,
  // ShowJoinButton(true) is called.
  useEffect(() => {
    if (openAskBuyin) {
      dispatch(showJoinButton(true));
    }
  }, [openAskBuyin, dispatch]);

  // joinButtonVisible lives on the shared global currentTable slice (see
  // JoinButton), so leaveTable's showJoinButton(false) — fired when ANY table
  // is exited — also blanks the CTA on the tables that remain open in grid
  // view. Re-assert the flag whenever the set of open tables changes so a
  // surviving cell restores its own Join button after a sibling exits.
  const openTablesKey = useAppSelector((state) =>
    Object.keys((state as any).tables || {})
      .sort()
      .join(',')
  );

  // Mirrors Unity BuyInView.cs:645-650 WaitForSeatAvailability — after
  // leaveTable dispatches ShowJoinButton(false), the coroutine waits
  // until the player is no longer seated, then re-enables the button.
  // Gate on gameID so we don't flip the flag before a table loads.
  // openTablesKey is a dep so exiting a sibling table (which cleared the
  // shared flag) re-triggers this on the tables that are still open.
  useEffect(() => {
    if (gameID && !isSeated) {
      dispatch(showJoinButton(true));
    }
  }, [gameID, isSeated, dispatch, openTablesKey]);

  // Every player action — the join prompt (post blind / wait for big blind), the
  // pre-actions (not-your-turn checkboxes), and the live Fold/Call/Raise buttons
  // — renders in the footer's action slot. The footer is the single home for all
  // of them.
  const content = useMemo<React.ReactNode>(() => {
    if (languageKey === 'GAME_MSG_BUSTED_PAUSE' && ingamePlayers > 1) {
      return <></>;
    }

    if (
      (!isSeated && !isMaintenance && tableType !== 'm' && tableType !== 't' && tableType !== '') ||
      isWaitingAndExit ||
      isRejoining
    ) {
      // JoinButton itself decides Join Table vs Join Waitlist (when the table
      // is full or already has a waitlist) vs render nothing, so it must mount
      // even on a full table. The old `noPlayers !== seats` guard suppressed it
      // whenever the table was full — exactly when the waitlist CTA is needed —
      // which is why the in-table "Join Waitlist" button never appeared.
      return <JoinButton />;
    }

    if (
      nextBBPlayer !== seatId &&
      ingamePlayers > 2 &&
      isWaiting &&
      (hand === '' || +hand > 3) &&
      tableType !== 'm' &&
      tableType !== 't'
    ) {
      // Post Blind / Wait For Big Blind → render in the footer's action slot.
      return <JoinActions />;
    }

    // EN-663 AC-2.2 / AC-2.5 / AC-3.1: a TWO-PHASE offer. The initiator is asked
    // first and alone; the responders are asked simultaneously the moment an
    // offer exists, and never at all if the initiator picks 1X.
    //
    // Both halves of that rule — who is asked, and which counts they may pick —
    // come from `offeredRunCounts`, which is the client mirror of what both
    // server writers enforce. An empty list means "not your decision right now",
    // so this single call is the mount gate AND the button set: they cannot
    // disagree about whether this seat is being asked.
    //
    // The pre-two-phase version gated on `runs.includes('v')` alone, which asked
    // every eligible seat at once (EN-10219) because the server then resolved
    // 2-vs-3 downward. AC-4.2 removed that resolve, so an unprompted responder
    // answering a question nobody had asked yet has nothing to agree WITH.
    //
    // `playerMultiRunVote !== '0'` is the dismiss-on-select half of AC-2.2 — "the
    // buttons dismiss exactly like normal action buttons". It reads the local
    // optimistic value, so the row goes on the click rather than a broadcast
    // later; `reconcileVote` restores it if the vote turns out not to have
    // landed.
    const runCounts =
      isSeated && !isWaiting && !isPlayerFolded && seatHasStakeInHand && playerMultiRunVote === '0'
        ? offeredRunCounts(game, seatId, maxRunCount)
        : [];

    if (runCounts.length > 0 && noPlayers > 1) {
      return (
        <>
          <WithholdExposure isInitiator={parseOffer(runs) === 0} />
          <MultiRunActions runCounts={runCounts} />
        </>
      );
    }

    // While running the boards out multiple times (run-it-twice, runs =
    // "<n>,<hand>,<run>" — comma-count 2) the hand is all-in and locked: no
    // player can act, so show no action panel and no pre-action controls at any
    // point during the run-out. The one exception is the "straddle next hand"
    // chip (EN-11023): it is for the next hand, and the run-out is the win
    // sequence it is offered in. It renders nothing without an offer.
    if (runs.split(',').length === 3) {
      return isSeated ? <StraddlePreActionButton /> : undefined;
    }

    if (isSeated && (!isWaiting || playerStatus === 4)) {
      // Pre-actions (not your turn) and live actions both render in the footer.
      return <PokerActions />;
    }

    return undefined;
  }, [
    languageKey,
    isJoining,
    isSeated,
    isMaintenance,
    tableType,
    nextBBPlayer,
    seatId,
    ingamePlayers,
    isWaiting,
    hand,
    isPlayerInactive,
    isWaitingAndExit,
    isRejoining,
    runs,
    noPlayers,
    isJoined,
    isPlayerFolded,
    playerStatus,
    // Scalar. It is what makes the buttons dismiss on the click rather than a
    // broadcast later (AC-2.2), so without this dep the row would outlive the
    // vote by a frame.
    playerMultiRunVote,
    // Also a scalar. It gates the multi-run panel and derives from this seat's
    // bet/ante, which do change mid-hand, so it has to be tracked.
    seatHasStakeInHand,
    // Scalar: the ops switch that withholds 3X (EN-10921) takes effect on the
    // next render, not the next broadcast.
    maxRunCount,
    // The initiator marker (AC-2.1). `runs` above covers the phase change; this
    // covers whose decision phase 1 is. It is stamped once per hand and the
    // hand-start reset clears it, so tracking it costs one scalar compare per
    // broadcast. `game` itself is deliberately NOT a dep: it is a new object on
    // every push and would re-render the whole action area each time.
    game.lastAllInPlayer
  ]);

  // The provider wraps the slot rather than living inside any one panel, so the
  // "taking longer than expected" banner outlives the swaps above: it can be
  // raised while MultiRunActions holds the slot during a vote window, and an
  // already-showing banner is not destroyed when a hand goes all-in and the
  // panel changes under it.
  //
  // The vote delivery watch (EN-10761) and the vote analytics (EN-10922) live
  // here for the same reason: the vote panel unmounts the moment a vote is
  // cast, which is exactly when waiting for its echo and its result begins.
  // The straddle tap watch (EN-11023) likewise: the chip unmounts with its offer
  // at the deal, which is when a too-late tap shows.
  return (
    <ServerTimeoutProvider>
      <VoteDeliveryWatch />
      <VoteAnalyticsWatch />
      <StraddleTapWatch>
        <TopAction actionContent={content} />
      </StraddleTapWatch>
    </ServerTimeoutProvider>
  );
};

export default UserActions;
